import { useEffect, useRef, useState } from 'react';
import { FcDocument } from 'react-icons/fc';
import { FaDownload } from 'react-icons/fa6';
import { GrAttachment } from 'react-icons/gr';
import EmojiPicker from 'emoji-picker-react';
import { BsEmojiSmile } from 'react-icons/bs';
import { PhoneIncoming, PhoneMissed, PhoneOff } from 'lucide-react';

import axios from 'axios';
import { CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import { GPT_API_KEY } from '../../constant/constant';
import chatgpt from '../../assets/chatgpt.svg';
import conversation from '../../assets/conversation.png';
import {
  chatGpt,
  getMessages,
  sendMessage,
  updateLastSender,
  updateUnreadCount,
  uploadFile,
} from '../../helper/backend_helper';

const socket = io('https://backend.timelesspuglia.com');

const ChatArea = ({ conversationDetail }) => {
  const [messages, setMessages] = useState([]); // To store messages

  const [message, setMessage] = useState(''); // To store the input value
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [sendMsgLoading, setSendMsgLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [response, setResponse] = useState('');
  const [gptLoading, setGptLoading] = useState(false);
  const [messageType, setMessageType] = useState('text');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [base64File, setBase64File] = useState(null);

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]; // Get first file
    if (file) {
      setSelectedFile(file);
      try {
        if (file.type.startsWith('image/')) {
          const base64String = await fileToBase64(file);
          setBase64File(base64String); // Store Base64 string
          setMessageType('image');
        } else if (file.type.startsWith('audio/')) {
          setMessageType('ptt'); // WhatsApp-style voice note
        } else if (file.type.startsWith('video/')) {
          setMessageType('video');
        } else if (file.type === 'application/pdf') {
          setMessageType('document');
        } else {
          // Optional: handle any unknown file type
          setMessageType('document');
        }

        // setMessageType('media');
      } catch (error) {
        console.error('Error converting file:', error);
      }
    }
  };

  // Remove file from preview
  const removeFile = () => {
    setSelectedFile(null);
  };
  const handleGenerate = async () => {
    if (!conversationDetail.thread_id) {
      toast.error("Thread doesn't exist, please send or receive any message");
      return;
    }
    // if (!prompt.trim()) return;
    setGptLoading(true);
    setResponse(''); // Clear previous response
    try {
      const data = {
        prompt,
        conversationId: conversationDetail._id,
        thread_id: conversationDetail.thread_id,
        bnb: conversationDetail?.user?.BnBShortName,
      };
      const res = await chatGpt(data);
      setResponse(res.data.response.trim());
      setMessage(res.data.response.trim());
      setPrompt(''); // Clear prompt input
    } catch (error) {
      console.error('Error fetching ChatGPT response:', error);
      setResponse('Error generating response. Please try again.');
    } finally {
      setGptLoading(false);
    }
  };

  const updateCount = async () => {
    try {
      await updateUnreadCount(conversationDetail?._id);
    } catch (error) {
      console.log(error);
    }
  };
  // Scroll to the bottom function
  useEffect(() => {
    const handleNewMessage = (data) => {
      updateCount();
      if (data?.conversationId === conversationDetail?._id) {
        console.log(data);
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            message: data.message,
            sender: data.sender,
            senderName: data.senderName,
            senderImage: data.senderImage,
            messageTime: data.messageTime,
            messageType: data.messageType,
            type: data.type,
            fromMe: data.fromMe,
          },
        ]);
      }
    };

    // Attach the socket listener
    socket.on('newMessage', handleNewMessage);

    // Cleanup the listener when the component unmounts
    return () => {
      socket.off('newMessage', handleNewMessage);
    };
  }, [conversationDetail?._id]);

  const fetchMessage = async () => {
    try {
      setLoading(true);
      setMessage('');
      const resposne = await getMessages(conversationDetail?._id);
      await updateUnreadCount(conversationDetail?._id);

      setMessages(resposne.data.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    fetchMessage();
  }, [conversationDetail?._id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSend = async () => {
    if (!message.trim() && !selectedFile && !base64File) return;

    try {
      setSendMsgLoading(true);
      let messageText = message.trim();
      if (messageType === 'image') messageText = base64File;
      if (messageType !== 'text' && messageType !== 'image') {
        const fileHeaders = {
          headers: {
            'content-type': 'multipart/form-data',
          },
        };

        const formData = new FormData();
        formData.append('file', selectedFile);
        const responseFile = await uploadFile(formData, fileHeaders);
        console.log(responseFile);
        messageText = process.env.REACT_APP_FILE_URL + responseFile.data.data.fileName;
      }
      console.log('messageText', messageText);

      const data = {
        message: messageText,
        messageType,
        type: 'WhatsApp',
        conversation: conversationDetail?._id,
        contactNo: conversationDetail?.contactNo,
        messageTime: new Date(),
      };

      const headers = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      const response = await sendMessage(data, headers);

      await updateLastSender(conversationDetail?._id);
      setMessages([
        ...messages,
        {
          message: response.data.data.message,
          messageType: response.data.data.messageType,
          sender: response.data.data.sender,
          senderName: response.data.data.senderName,
          senderImage: response.data.data.senderImage,
          messageTime: response.data.data.messageTime,
          type: response.data.data.type,
          fromMe: true,
        },
      ]); // Add the new message to the list
      setMessage('');
      setSelectedFile(null);
      setSendMsgLoading(false);
      setMessageType('text');
    } catch (error) {
      setSendMsgLoading(false);
      console.log(error);
    }

    // if (message.trim()) {
    //   setMessages([...messages, { message, type: 'right' }]); // Add the new message to the list
    //   setMessage(''); // Clear the input field
    // }
  };

  return (
    <div className="p-4 flex h-[90vh] flex-col">
      {/* Chat Header */}
      {conversationDetail?.user ? (
        <div className="mb-4 flex justify-between">
          <h3 className="font-bold text-lg">{conversationDetail?.user?.guestName}</h3>
          <div className="flex flex-col items-end">
            <p className="text-sm text-gray-500">{conversationDetail?.contactNo}</p>
            <p className="text-sm text-gray-500">{conversationDetail?.user?.email}</p>
          </div>
        </div>
      ) : (
        <div className="mb-4 flex justify-between">
          <h3 className="font-bold text-lg">{conversationDetail?.conversation_name}</h3>
          <div className="flex flex-col items-end">
            <p className="text-sm text-gray-500">{conversationDetail?.contactNo}</p>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center h-full">
          <CircularProgress size="2rem" color="error" />
        </div>
      )}
      {messages?.length === 0 && !loading && (
        <div className="flex flex-col justify-center items-center h-full gap-1 px-6">
          <img className="w-auto h-1/4  object-cover" src={conversation} alt="John Smith" />
          <h1 className="text-lg font-bold text-gray-900">No messages yet</h1>
          <h1 className="text-sm font-normal text-gray-500 text-center">
            Looks like you haven't started a conversation with any of our professionals
          </h1>
        </div>
      )}
      <div className="flex-1 overflow-y-auto  rounded  scrollbar-hide space-y-3 bg-gray-100">
        {messages?.map((msg, index) => (
          <>
            {/* <div
              key={index}
              style={{ whiteSpace: 'pre-wrap' }}
              className={`bg-white 
              ${msg?.sender === 'admin' ? 'ml-auto' : 'mr-auto'}
               text-black font-thin text-xs p-3 rounded-xl max-w-xs`}
            >
              {msg?.message}
            </div> */}
            <div
              key={index}
              style={{ whiteSpace: 'pre-wrap' }}
              className={`bg-white 
    ${msg?.fromMe ? 'ml-auto' : 'mr-auto'}
    text-black font-thin text-xs p-2 rounded-xl max-w-xs`}
            >
              {/* Text Message */}
              {msg?.messageType === 'text' && <span className="break-words">{msg?.message}</span>}

              {/* Call Info */}
              {msg?.messageType === 'info' &&
                (() => {
                  const callInfo = getCallInfo(msg);
                  if (callInfo) {
                    const { icon: Icon, text, iconColor, bgColor, borderColor } = callInfo;
                    return (
                      <div
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${bgColor} ${borderColor} w-full`}
                      >
                        <Icon className={`w-4 h-4 ${iconColor}`} />
                        <span className="text-sm font-medium text-gray-700">{text}</span>
                      </div>
                    );
                  }
                  return null;
                })()}

              {/* Sticker */}
              {msg?.messageType === 'sticker' && (
                <img src={msg?.message} alt="Sticker" className="w-1/3 text-center mx-auto" />
              )}

              {/* Image */}
              {msg?.messageType === 'image' && (
                <a href={msg?.message} target="_blank" rel="noopener noreferrer">
                  <img src={msg?.message} alt="imge" className="w-full max-h-[200px] object-cover rounded-lg" />
                </a>
              )}

              {/* Document (PDF) */}
              {msg?.messageType === 'document' && (
                <div
                  // href={msg?.message}
                  // target="_blank"
                  // rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 flex items-center justify-between w-full p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <FcDocument size={20} className="flex-shrink-0" />
                    <span className=" max-w-[200px]">{msg?.filename || 'Document'}</span>
                  </div>
                  <a href={msg?.message} download={msg?.filename || 'Document'} className="flex-shrink-0">
                    <FaDownload size={20} className="text-gray-500 hover:text-gray-700 cursor-pointer" />
                  </a>
                </div>
              )}

              {/* Audio (ptt) */}
              {msg?.messageType === 'ptt' && (
                <audio controls className="w-full">
                  <track default kind="captions" srcLang="en" src={msg?.message} />
                  <source src={msg?.message} type="audio/ogg" />
                  Your browser does not support the audio element.
                </audio>
              )}

              {/* Video */}
              {msg?.messageType === 'video' && (
                <video controls className="w-full max-h-[200px] rounded-lg">
                  <track default kind="captions" srcLang="en" src={msg?.message} />
                  <source src={msg?.message} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
            {msg?.fromMe ? (
              <div className="flex items-center px-2 ml-auto max-w-xs justify-between">
                <h1 className="text-[8px] -mt-2">{msg?.type}</h1>
                <h1 className="text-[8px] -mt-2">{msg?.senderName || msg?.sender}</h1>
                <h1 className="text-[8px] -mt-2">{new Date(msg?.messageTime).toLocaleString()}</h1>
              </div>
            ) : (
              <div className="flex items-center px-2 mr-auto max-w-xs justify-between">
                <h1 className="text-[8px] -mt-2">WhatsApp</h1>
                <h1 className="text-[8px] -mt-2">{msg?.senderName || msg?.sender}</h1>
                <h1 className="text-[8px] -mt-2">{new Date(msg?.messageTime).toLocaleString()}</h1>
              </div>
            )}
          </>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Input */}
      {messages?.length > 0 && (
        <div className="sticky bottom-0">
          <div className="mt-4 relative">
            {/* File Previews */}
            {selectedFile && (
              <div className="mb-2 flex flex-wrap gap-2">
                {/* {selectedFiles.map((file, index) => ( */}
                <div
                  // key={index}
                  className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg"
                >
                  <span className="text-xs truncate max-w-[150px]">{selectedFile.name}</span>
                  <button onClick={() => removeFile()} className="text-red-500 hover:text-red-700">
                    ×
                  </button>
                </div>
                {/* ))} */}
              </div>
            )}
            <div className="flex items-center w-full">
              {/* Icons container */}
              <div className="absolute ml-2 left-2 flex items-center gap-2">
                <div className="">
                  <label htmlFor="fileInput" className="cursor-pointer">
                    <GrAttachment size={16} className="text-gray-500 hover:text-gray-700" title="Upload file" />
                    <input
                      type="file"
                      id="fileInput"
                      accept=".pdf,image/*,audio/*,video/*"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>

                <BsEmojiSmile
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  size={16}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                  title="Add emoji"
                />
              </div>

              {/* Textarea */}
              <textarea
                rows="3"
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your message..."
                className="w-full  p-2 pl-2 text-xs font-light rounded-2xl outline-none placeholder:text-xs placeholder:font-light"
              />
            </div>
            {showEmojiPicker && (
              <div className="absolute bottom-12 left-0 z-20">
                <EmojiPicker
                  onEmojiClick={(emojiObject) => {
                    setMessage((prev) => prev + emojiObject.emoji);
                    setShowEmojiPicker(false);
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex my-1 space-x-2">
            <button className="border border-black flex-1 px-1 py-1 text-black rounded-full hover:bg-gray-100 text-xs">
              OTA
            </button>
            <button className="border border-black flex-1 px-1 py-1 text-black rounded-full hover:bg-gray-100 text-xs">
              Email
            </button>
            <button className="border border-black flex-1 px-1 py-1 text-black rounded-full hover:bg-gray-100 text-xs">
              WhatsApp
            </button>
            <button className="border border-black flex-1 px-1 py-1 text-black rounded-full hover:bg-gray-100 text-xs">
              SMS
            </button>
            {sendMsgLoading ? (
              <button className="border border-black bg-black flex-[2] px-1 py-1 text-white rounded-full text-xs">
                Sending...
              </button>
            ) : (
              <button
                onClick={handleSend}
                className="border border-black bg-black flex-[2] px-1 py-1 text-white rounded-full text-xs"
              >
                Send Message
              </button>
            )}
          </div>

          <div className="my-3 flex gap-2">
            <textarea
              rows="3"
              disabled
              type="text"
              placeholder="Type your prompt..."
              className="w-full p-2 text-xs font-light rounded-2xl outline-none placeholder:text-xs placeholder:font-light"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <div className="flex flex-col items-center justify-center gap-1">
              <img className="w-7 h-7  object-cover" src={chatgpt} alt="John Smith" />
              <button
                onClick={handleGenerate}
                disabled={gptLoading}
                className="border border-black bg-black  px-8 max-sm:px-4 py-1 text-white rounded-full  text-xs"
              >
                {gptLoading ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default ChatArea;

const getCallInfo = (msg) => {
  switch (msg?.subType) {
    case 'call/miss':
      return {
        icon: PhoneMissed,
        text: 'Missed call',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-100',
      };
    case 'call/reject':
      return {
        icon: PhoneOff,
        text: 'Call declined',
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-100',
      };
    case 'call/accept':
      return {
        icon: PhoneIncoming,
        text: 'Call answered',
        iconColor: 'text-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-100',
      };
    default:
      return null;
  }
};
